import { motion } from "framer-motion";
import { ShieldCheck, Handshake, Certificate, Globe as GlobeIcon } from "@phosphor-icons/react";
import { MaskedLines, Reveal } from "@/components/Motion";
import { COMPANY } from "@/data/content";

const CHAPTERS = [
  {
    no: "01",
    title: "Company background",
    body:
      "Top C Group Sdn Bhd is a Malaysia-based trading and business services company. We operate as a sourcing and procurement intermediary and a third-party operations service provider — bridging trusted manufacturers in China with international brand partners.",
  },
  {
    no: "02",
    title: "Malaysia registered",
    body:
      `Officially registered with the Companies Commission of Malaysia (SSM) under ${COMPANY.ssm}. Our registration underpins every partnership with accountability, transparent trade documentation and a compliant cross-border operating base in Johor Bahru.`,
  },
  {
    no: "03",
    title: "Our mission",
    body:
      "To be the dependable operational backbone for growing consumer brands — removing the friction of sourcing, supply and day-to-day operations so our partners can focus on building their brand and serving their customers.",
  },
];

const TRUST_POINTS = [
  { icon: ShieldCheck, title: "Malaysia registered", text: "A legally registered entity (SSM 202601021906) you can verify and trust." },
  { icon: GlobeIcon, title: "International supply chain", text: "Established routes to the US, UK, Europe, Australia and New Zealand." },
  { icon: Handshake, title: "Dedicated support team", text: "A single, accountable team managing your account end-to-end." },
  { icon: Certificate, title: "Quality at source", text: "Consumer-safety products inspected and verified before they ship." },
];

const About = () => (
  <div data-testid="page-about">
    <section className="bg-[#050a11] text-white pt-40 pb-24 md:pb-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <p className="overline text-[#9dc0ff] mb-8">About us</p>
        <h1 className="font-display font-light text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.92] tracking-tight max-w-4xl">
          <MaskedLines lines={["The partner behind", "your supply chain."]} />
        </h1>
      </div>
    </section>

    {/* clipped architecture photo */}
    <section className="bg-[#f8f9fa]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 -mt-16 md:-mt-24 relative z-10">
        <Reveal>
          <div className="overflow-hidden border border-black/10" style={{ clipPath: "polygon(0 0, 100% 0, 100% 88%, 0 100%)" }}>
            <img
              src="https://images.pexels.com/photos/946310/pexels-photo-946310.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Corporate architecture"
              className="w-full h-[42vh] md:h-[56vh] object-cover photo-grayscale"
            />
          </div>
        </Reveal>
      </div>
    </section>

    {/* manifesto */}
    <section data-testid="about-manifesto" className="bg-[#f8f9fa] py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid md:grid-cols-12 gap-y-20 gap-x-10">
        <div className="md:col-span-4">
          <div className="md:sticky md:top-32">
            <p className="overline text-[#0b2046]/50 mb-5">Who we are</p>
            <h2 className="font-display font-light text-4xl md:text-5xl leading-none tracking-tight text-[#050a11]">
              Built on trust, run with precision.
            </h2>
          </div>
        </div>
        <div className="md:col-span-8 space-y-16 md:space-y-24">
          {CHAPTERS.map((c) => (
            <Reveal key={c.no} className="grid grid-cols-1 sm:grid-cols-12 gap-6 border-t border-black/10 pt-8">
              <div className="sm:col-span-3">
                <span className="font-display font-black text-6xl md:text-7xl text-[#0b2046] leading-none">{c.no}</span>
              </div>
              <div className="sm:col-span-9">
                <h3 className="font-display text-2xl md:text-3xl tracking-tight text-[#050a11]">{c.title}</h3>
                <p className="mt-4 text-[#4b5563] leading-relaxed max-w-xl">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* why partners trust us */}
    <section data-testid="about-trust" className="bg-[#050a11] text-white py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Reveal>
          <p className="overline text-[#9dc0ff] mb-6">Why partners trust us</p>
          <h2 className="font-display font-light text-4xl md:text-6xl leading-none tracking-tight max-w-3xl">
            Accountability at every step.
          </h2>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {TRUST_POINTS.map((t, i) => (
            <motion.div
              key={t.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="bg-[#050a11] p-8 md:p-12 hover:bg-[#0a121f] transition-colors"
            >
              <t.icon size={34} weight="duotone" className="text-[#9dc0ff]" />
              <h3 className="font-display text-xl md:text-2xl tracking-tight mt-8">{t.title}</h3>
              <p className="mt-3 text-white/60 leading-relaxed max-w-sm">{t.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  </div>
);

export default About;
